import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { getMostSearchedKeywords } from '../../services/api';

const SearchAnalyticsPage = () => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchMostSearched = async () => {
      setLoading(true);
      try {
        const response = await getMostSearchedKeywords({ page: currentPage, limit: 20 });
        setKeywords(response.data.keywords || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } catch (error) {
        console.error('Error fetching most searched keywords:', error);
        toast.error('Failed to load search analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchMostSearched();
  }, [currentPage]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Searched Items</h1>
        <p className="text-admin-slate-600 dark:text-admin-slate-400">
          All keywords customers have searched for, with the most searched keywords at the top
        </p>
      </div>

      {/* Most Searched Table */}
      <div className="bg-white dark:bg-admin-slate-800 rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
            <thead className="bg-admin-slate-50 dark:bg-admin-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Keyword
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-admin-slate-500 dark:text-admin-slate-300 uppercase tracking-wider">
                  Times Searched
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-admin-slate-800 divide-y divide-admin-slate-200 dark:divide-admin-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-ucla-500 mx-auto"></div>
                  </td>
                </tr>
              ) : keywords.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-center text-admin-slate-500 dark:text-admin-slate-400">
                    No searches have been recorded yet
                  </td>
                </tr>
              ) : (
                keywords.map((item, index) => (
                  <tr key={item._id} className="hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-admin-slate-500 dark:text-admin-slate-400">
                      #{(currentPage - 1) * 20 + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">
                      {item.keyword}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-admin-ucla-100 text-admin-ucla-800 dark:bg-admin-ucla-900/30 dark:text-admin-ucla-300">
                        {item.count}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-admin-slate-200 dark:border-admin-slate-700 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 bg-white dark:bg-admin-slate-800 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700"
            >
              Previous
            </button>
            <span className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 bg-white dark:bg-admin-slate-800 border border-admin-slate-300 dark:border-admin-slate-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchAnalyticsPage;
